import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { KeyRound, Loader2, Settings2, Users, MessageSquareHeart, CalendarClock } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";
import VideoConfig from "@/components/VideoConfig";
import BankAccountsConfig from "@/components/BankAccountsConfig";
import AutoPaymentConfig from "@/components/AutoPaymentConfig";

const FIELDS = [
  ["nome_igreja", "Nome exibido da igreja *", "Igreja Unida Deus Proverá"],
  ["titulo_pagina", "Título da página *", "Painel de Pagamentos e Contribuições"],
  ["chave_pix_padrao", "Chave Pix padrão", "E-mail, CNPJ, telefone ou chave aleatória"],
  ["beneficiario_padrao", "Beneficiário padrão", "Ex.: Igreja Unida Deus Proverá"],
  ["cnpj_padrao", "CNPJ padrão", "00.000.000/0000-00"],
  ["whatsapp", "WhatsApp da igreja", "(11) 99999-9999"],
];

export default function ConfigPanel() {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6 items-start">
      <SiteConfig />
      <PasswordConfig />
    </div>
  );
}

function SiteConfig() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/config").then((r) => setForm(r.data)).catch(() => toast.error("Não foi possível carregar as configurações."));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome_igreja.trim() || !form.titulo_pagina.trim()) return toast.error("Nome da igreja e título são obrigatórios.");
    setSaving(true);
    try {
      const r = await api.put("/admin/config", form);
      setForm(r.data);
      toast.success("Configurações salvas! A página pública já reflete as mudanças.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="bg-white rounded-2xl border p-8 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-blue-800" /></div>;

  return (
    <form onSubmit={submit} data-testid="site-config-panel" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-blue-800 flex items-center justify-center shrink-0"><Settings2 className="w-6 h-6 text-white" /></div>
        <div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900">Configurações do site</h2>
          <p className="text-sm sm:text-base text-slate-500">Textos e dados padrão usados na página pública e nas novas metas.</p>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {FIELDS.map(([k, label, ph]) => (
          <div key={k} className={k === "nome_igreja" || k === "titulo_pagina" ? "sm:col-span-2" : ""}>
            <Label className="text-base font-semibold text-slate-700">{label}</Label>
            <Input data-testid={`config-${k}`} value={form[k] || ""} onChange={set(k)} placeholder={ph} className="mt-1.5 h-12 text-base rounded-xl" />
          </div>
        ))}
        <div className="sm:col-span-2">
          <Label className="text-base font-semibold text-slate-700">Texto de apresentação</Label>
          <Textarea data-testid="config-texto_apresentacao" value={form.texto_apresentacao || ""} onChange={set("texto_apresentacao")} className="mt-1.5 text-base rounded-xl min-h-[80px]" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-base font-semibold text-slate-700">Mensagem após o pagamento</Label>
          <Textarea data-testid="config-mensagem_pos_pagamento" value={form.mensagem_pos_pagamento || ""} onChange={set("mensagem_pos_pagamento")} className="mt-1.5 text-base rounded-xl min-h-[70px]" placeholder="Deus abençoe sua generosidade!" />
        </div>
        <div className="sm:col-span-2 flex items-center justify-between gap-4 bg-amber-50 rounded-xl px-4 py-3 border border-amber-100">
          <div className="flex items-start gap-3">
            <CalendarClock className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
            <div>
              <Label className="text-base font-semibold text-slate-800">Vencimentos próximos primeiro</Label>
              <p className="text-sm text-slate-500">Ordena automaticamente a página pública: as metas que vencem primeiro aparecem nas primeiras posições. Desligado, vale a ordem manual definida com as setas na aba Metas.</p>
            </div>
          </div>
          <Switch data-testid="config-ordenar_por_vencimento" checked={!!form.ordenar_por_vencimento} onCheckedChange={(v) => setForm((f) => ({ ...f, ordenar_por_vencimento: v }))} />
        </div>
        <div className="sm:col-span-2 flex items-center justify-between gap-4 bg-sky-50 rounded-xl px-4 py-3 border border-sky-100">
          <div className="flex items-start gap-3">
            <Users className="w-5 h-5 text-sky-700 mt-0.5 shrink-0" />
            <div>
              <Label className="text-base font-semibold text-slate-800">Mural de Gratidão</Label>
              <p className="text-sm text-slate-500">Mostra na página pública os primeiros nomes de quem já ajudou cada meta (apenas contribuições confirmadas).</p>
            </div>
          </div>
          <Switch data-testid="config-mural_ativo" checked={!!form.mural_ativo} onCheckedChange={(v) => setForm((f) => ({ ...f, mural_ativo: v }))} />
        </div>
        <VideoConfig form={form} setForm={setForm} />
        <BankAccountsConfig form={form} setForm={setForm} />
        <AutoPaymentConfig form={form} setForm={setForm} />
        <div className="sm:col-span-2 flex items-center justify-between gap-4 bg-rose-50 rounded-xl px-4 py-3 border border-rose-100">
          <div className="flex items-start gap-3">
            <MessageSquareHeart className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
            <div>
              <Label className="text-base font-semibold text-slate-800">Testemunhos em vídeo</Label>
              <p className="text-sm text-slate-500">Mostra o carrossel de testemunhos abaixo das metas. Cadastre os vídeos na aba Testemunhos.</p>
            </div>
          </div>
          <Switch data-testid="config-testemunhos_ativo" checked={!!form.testemunhos_ativo} onCheckedChange={(v) => setForm((f) => ({ ...f, testemunhos_ativo: v }))} />
        </div>
      </div>
      <button type="submit" data-testid="config-site-submit" disabled={saving} className="mt-6 w-full py-4 rounded-xl bg-blue-800 hover:bg-blue-700 disabled:opacity-60 text-white font-bold text-lg shadow-md transition-colors flex items-center justify-center gap-2">
        {saving && <Loader2 className="w-5 h-5 animate-spin" />} SALVAR CONFIGURAÇÕES
      </button>
    </form>
  );
}

function PasswordConfig() {
  const [atual, setAtual] = useState("");
  const [nova, setNova] = useState("");
  const [confirma, setConfirma] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nova.length < 6) return toast.error("A nova senha deve ter pelo menos 6 caracteres.");
    if (nova !== confirma) return toast.error("A confirmação não confere com a nova senha.");
    setSaving(true);
    try {
      await api.post("/admin/change-password", { senha_atual: atual, nova_senha: nova });
      toast.success("Senha alterada com sucesso!");
      setAtual(""); setNova(""); setConfirma("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="config-panel" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-slate-800 flex items-center justify-center shrink-0"><KeyRound className="w-6 h-6 text-white" /></div>
        <div>
          <h2 className="font-heading text-xl font-bold text-slate-900">Senha de acesso</h2>
          <p className="text-sm text-slate-500">Altere a senha do seu login.</p>
        </div>
      </div>
      <div className="mt-6 space-y-4">
        <div><Label className="text-base font-semibold text-slate-700">Senha atual</Label><Input data-testid="config-current-password" type="password" value={atual} onChange={(e) => setAtual(e.target.value)} className="mt-1.5 h-12 text-base rounded-xl" autoComplete="current-password" /></div>
        <div><Label className="text-base font-semibold text-slate-700">Nova senha</Label><Input data-testid="config-new-password" type="password" value={nova} onChange={(e) => setNova(e.target.value)} className="mt-1.5 h-12 text-base rounded-xl" autoComplete="new-password" /></div>
        <div><Label className="text-base font-semibold text-slate-700">Confirmar nova senha</Label><Input data-testid="config-confirm-password" type="password" value={confirma} onChange={(e) => setConfirma(e.target.value)} className="mt-1.5 h-12 text-base rounded-xl" autoComplete="new-password" /></div>
        <button type="submit" data-testid="config-submit" disabled={saving} className="w-full py-4 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-white font-bold text-lg shadow-md transition-colors flex items-center justify-center gap-2">
          {saving && <Loader2 className="w-5 h-5 animate-spin" />} SALVAR NOVA SENHA
        </button>
      </div>
    </form>
  );
}
